import React, { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Download } from "lucide-react";
import { labAuditLog } from "../lib/api";

const ROLE_LABEL = { admin: "Coach", doctor: "Doctor", client: "Member", unknown: "Unknown" };

export default function LabAuditLog({ labId, auth, compact = false }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(!compact);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await labAuditLog(labId, auth);
        if (active) setEntries(data);
      } catch (err) {
        if (active) setError(err?.response?.data?.detail || "Could not load access log");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [labId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (compact && !open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] eyebrow text-[var(--lc-muted)] hover:text-[var(--lc-ink)] inline-flex items-center gap-1.5"
        data-testid={`lab-audit-open-${labId}`}
      >
        <ShieldCheck className="h-3 w-3" /> Access log · {entries.length || "—"}
      </button>
    );
  }

  return (
    <div className="mt-3 border-t border-[var(--lc-line)] pt-3" data-testid={`lab-audit-${labId}`}>
      <div className="eyebrow text-[var(--lc-muted)] mb-2 flex items-center gap-1.5">
        <ShieldCheck className="h-3 w-3" /> Access log
        {entries.length > 0 && <span className="text-[var(--lc-ink)]">· {entries.length}</span>}
      </div>
      {loading ? (
        <div className="text-xs text-[var(--lc-muted)] inline-flex items-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading...
        </div>
      ) : error ? (
        <div className="text-xs text-red-700" data-testid={`lab-audit-error-${labId}`}>{error}</div>
      ) : entries.length === 0 ? (
        <div className="text-xs text-[var(--lc-muted)]">No downloads yet.</div>
      ) : (
        <ul className="divide-y divide-[var(--lc-line)]" data-testid={`lab-audit-list-${labId}`}>
          {entries.map((e) => (
            <li key={e.id} className="py-2 flex items-center justify-between gap-3 text-xs" data-testid={`lab-audit-entry-${e.id}`}>
              <span className="inline-flex items-center gap-2">
                <Download className="h-3 w-3 text-[var(--lc-moss-dark)]" />
                <span className="font-semibold">{e.actor_name}</span>
                <span className="eyebrow px-1.5 py-0.5 bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-sm text-[9px]">
                  {ROLE_LABEL[e.actor_role] || e.actor_role}
                </span>
              </span>
              <span className="text-[var(--lc-muted)]">
                {new Date(e.downloaded_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
